import { Injectable } from '@angular/core'
import { catchError, map, Observable, of, tap } from 'rxjs'
import { WordPairDicService } from 'src/app/service/data/word-pair-dic.service'
import { BookmarkStateService } from 'src/app/service/state/bookmark-state.service'
import { DictionaryStateService } from 'src/app/service/state/dictionary-state.service'
import { HomeStateService } from 'src/app/service/state/home-state.service'
import { LibraryStateService } from 'src/app/service/state/library-state.service'
import { HomeElemType } from 'src/app/shared/models/main/home-elem'
import { CoreCommand } from '../base/core-command'
import { CommandResultService } from '../decorators/command-result.decorator'

export interface CategoryData {
  id: number
  label: string
}

export interface TagData {
  id: number
  label: string
}

export interface DictionaryData {
  id: number
  label: string
}
export interface WordData {
  language: string
  article?: string
  word: string
  pos?: string
  definition?: string
  example?: string
  category?: CategoryData
  tags?: TagData[]
}

export interface EditWordPairDicArgs {
  id: number
  word1: WordData
  word2?: WordData
  privacy: string
  dictionaries?: { id: number; label: string }[]
  dictionary_title?: string
  dictionary_description?: string
  collection_name?: string
}

@Injectable({
  providedIn: 'root',
})
export class EditWordPairDicCommand extends CoreCommand<EditWordPairDicArgs, any> {
  readonly id = 'edit-word-pair-dic'

  constructor(
    private commandResultService: CommandResultService,
    private wordPairDicService: WordPairDicService,
    private libraryState: LibraryStateService,
    private homeState: HomeStateService,
    private dictionaryState: DictionaryStateService,
    private bookmarkState: BookmarkStateService,
  ) {
    super()
  }

  override checkArguments(args?: EditWordPairDicArgs): boolean {
    return !!args && !!args.id
  }

  protected doExecute(args?: EditWordPairDicArgs): Observable<boolean> {
    return this.wordPairDicService.edit(args!).pipe(
      tap(result => this.applyStateUpdates(result)),
      tap(result => {
        this.emitResult({ success: result.success, message: result.message, data: result.data })
        this.commandResultService.handleResult(this)
      }),
      map(result => result.success),
      catchError(error => {
        this.emitResult({
          success: false,
          message: error instanceof Error ? error.message : 'Failed to edit word/pair',
        })
        this.commandResultService.handleError(error)
        return of(false)
      }),
    )
  }

  private applyStateUpdates(result: any): void {
    // Update library and home states
    if (result.data.word_1) {
      this.libraryState.updateWord(result.data.word_1)

      result.data.word_1.type = HomeElemType.Word
      this.homeState.updateItem(result.data.word_1)

      this.dictionaryState.updateWord(result.data.word_1)

      // Update bookmark state for word_1
      const bookmarkItem = { ...result.data.word_1, type: 'word' as const }
      this.bookmarkState.updateBookmarkItem(bookmarkItem)
    }
    if (result.data.word_2) {
      this.libraryState.updateWord(result.data.word_2)

      result.data.word_2.type = HomeElemType.Word
      this.homeState.updateItem(result.data.word_2)

      this.dictionaryState.updateWord(result.data.word_2)

      // Update bookmark state for word_2
      const bookmarkItem = { ...result.data.word_2, type: 'word' as const }
      this.bookmarkState.updateBookmarkItem(bookmarkItem)
    }
    if (result.data.pair) {
      this.libraryState.updatePair(result.data.pair)

      result.data.pair.type = HomeElemType.Pair
      this.homeState.updateItem(result.data.pair)

      this.dictionaryState.updatePair(result.data.pair)

      // Update bookmark state for pair
      const bookmarkItem = { ...result.data.pair, type: 'pair' as const }
      this.bookmarkState.updateBookmarkItem(bookmarkItem)
    }
    if (result.data.dictionary) {
      this.libraryState.updateDictionary(result.data.dictionary)

      result.data.dictionary.type = HomeElemType.Dictionary
      this.homeState.updateItem(result.data.dictionary)

      this.dictionaryState.updateDictionary(result.data.dictionary)

      // Update bookmark state for dictionary
      const bookmarkItem = { ...result.data.dictionary, type: 'dictionary' as const }
      this.bookmarkState.updateBookmarkItem(bookmarkItem)
    }

    if (result.data.collection) {
      this.bookmarkState.updateBookmarkGroup(result.data.collection)
    }
  }
}
